import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip, TooltipProps } from 'components/UI/V3/Tooltip/Tooltip';
import tooltipStyles from 'components/UI/V3/Tooltip/Tooltip.module.css';
import clsx from 'clsx';

/**
 * Wrapper for Tooltip that renders the tooltip content in a portal
 * to avoid overflow and z-index issues when inside a Drawer
 */
export const DrawerTooltip: React.FC<TooltipProps> = props => {
    const [isVisible, setIsVisible] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const childRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const hiddenTooltipRef = useRef<HTMLDivElement>(null);

    const placement = props.placement || 'top';

    const calculatePosition = () => {
        if (!childRef.current || !hiddenTooltipRef.current) return;

        const childRect = childRef.current.getBoundingClientRect();
        const tooltipRect = hiddenTooltipRef.current.getBoundingClientRect();
        const spacing = 8;

        let top = 0;
        let left = 0;

        switch (placement) {
            case 'top':
                top = childRect.top - tooltipRect.height - spacing;
                left = childRect.left + (childRect.width - tooltipRect.width) / 2;
                break;
            case 'bottom':
                top = childRect.bottom + spacing;
                left = childRect.left + (childRect.width - tooltipRect.width) / 2;
                break;
            case 'left':
                top = childRect.top + (childRect.height - tooltipRect.height) / 2;
                left = childRect.left - tooltipRect.width - spacing;
                break;
            case 'right':
                top = childRect.top + (childRect.height - tooltipRect.height) / 2;
                left = childRect.right + spacing;
                break;
        }

        setPosition({ top, left });
    };

    useEffect(() => {
        // Observe the actual Tooltip component to detect when it becomes visible
        const checkTooltip = () => {
            // Find the tooltip element created by the Tooltip component
            const tooltipElement = childRef.current?.parentElement?.querySelector(
                `[class*="tooltip"][data-show="true"]`
            ) as HTMLElement | null;

            if (tooltipElement) {
                const isTooltipVisible = tooltipElement.getAttribute('data-show') === 'true';
                setIsVisible(isTooltipVisible);
                if (isTooltipVisible) {
                    requestAnimationFrame(() => {
                        requestAnimationFrame(calculatePosition);
                    });
                }
            } else {
                setIsVisible(false);
            }
        };

        const observer = new MutationObserver(checkTooltip);
        const interval = setInterval(checkTooltip, 100);

        // Observe the document for tooltip changes
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['data-show']
        });

        return () => {
            observer.disconnect();
            clearInterval(interval);
        };
    }, []);

    useEffect(() => {
        if (!isVisible) return;

        const updatePosition = () => {
            calculatePosition();
        };

        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);

        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isVisible]);

    return (
        <>
            <div
                ref={childRef}
                style={{ display: 'inline-flex', position: 'relative' }}
            >
                <Tooltip {...props}>{props.children}</Tooltip>
                {/* Hidden tooltip to get dimensions */}
                <div
                    ref={hiddenTooltipRef}
                    className={clsx(
                        tooltipStyles.tooltip,
                        tooltipStyles[`tooltip${placement.charAt(0).toUpperCase() + placement.slice(1)}`],
                        props.className
                    )}
                    style={{
                        position: 'fixed',
                        visibility: 'hidden',
                        pointerEvents: 'none',
                        opacity: 0,
                        top: '-9999px',
                        left: '-9999px'
                    }}
                >
                    {props.content}
                </div>
            </div>
            {/* Hide the original tooltip and show our portal version */}
            <style>{`
                div[class*="tooltip"][data-show="true"]:not([data-portal-tooltip]) {
                    visibility: hidden !important;
                    opacity: 0 !important;
                }
            `}</style>
            {isVisible &&
                createPortal(
                    <div
                        ref={tooltipRef}
                        data-portal-tooltip="true"
                        className={clsx(
                            tooltipStyles.tooltip,
                            tooltipStyles[`tooltip${placement.charAt(0).toUpperCase() + placement.slice(1)}`],
                            props.className
                        )}
                        style={{
                            top: position.top,
                            left: position.left,
                            zIndex: 2000
                        }}
                        data-show={isVisible}
                    >
                        {props.content}
                    </div>,
                    document.body
                )}
        </>
    );
};
